import axios from "axios";
import {
  API_REGION_FOR_STAGE,
  INVOKE_API_ID,
  BackendRegion,
  FrontendRegion
} from "../common/AccountData";
import { ProgramData } from "../common/Program";
import { AuthConfig } from "../lib/AuthConfig";
import { getLocalStorage } from "./dom";
import { getRegion } from "../common/MarketplaceConfig";

export interface FetchProgramProps {
  program: string;
  programId: string;
  region: string;
}

export interface FetchAllProgramsProps {
  program: string;
  region: string;
}

const EU_MARKETPLACE_TO_REGION: Record<string, BackendRegion> = {
  "A1PA6795UKMFR9": "FRA", // DE
  "A1F83G8C2ARO7P": "ZAZ", // GB
  "A13V1IB3VIYZZH": "ZAZ", // FR
  "APJ6JRA9NG5V4": "ZAZ",  // IT
  "A1RKKUPIHCS9HS": "ZAZ",  // ES
};

/**
 * Resolves the routing region for write APIs that have a marketplace ID.
 * ACS returns "EU" for all EU marketplaces; this function remaps to the
 * specific backend region (ZAZ or FRA) based on the marketplace.
 *
 * In beta, all backend regions resolve to the same shared API Gateway,
 * so the per-marketplace mapping is effectively a no-op there.
 */
export function resolveRoutingRegion(stage: string, marketplaceId: string): BackendRegion {
  const acsRegion = getRegion(stage, marketplaceId)! as FrontendRegion;
  if (acsRegion !== "EU") {
    return acsRegion;
  }
  return EU_MARKETPLACE_TO_REGION[marketplaceId] ?? "ZAZ";
}

export async function fetchStage() {
  return await axios.get("/settings.json");
}

const authAxios = () =>
  axios.create({
    headers: {
      authorization: getLocalStorage().getItem("idToken")
    }
  });

function buildBaseUrl(region: BackendRegion, stage: string): string {
  const awsRegion = API_REGION_FOR_STAGE[stage][region];
  const apiId = INVOKE_API_ID[stage][region];
  return `https://${apiId}.execute-api.${awsRegion}.amazonaws.com/prod`;
}

export async function fetchProgram(props: FetchProgramProps, stage: string) {
  const region: BackendRegion = props.region === "EU" ? "ZAZ" : (props.region as BackendRegion);
  const apiURL = buildBaseUrl(region, stage);
  return await authAxios().get(`${apiURL}/${props.program}/${props.programId}`);
}

export async function updateProgram(data: ProgramData, stage: string) {
  const region = resolveRoutingRegion(stage, data.marketplaceId);
  data.lastActor = AuthConfig.getAlias();
  const apiURL = buildBaseUrl(region, stage)
    .concat(`/${data.program}`)
    .concat(`/${data.programId}`);
  return await authAxios().post(apiURL, data);
}

export async function createProgram(data: ProgramData, stage: string) {
  const region = resolveRoutingRegion(stage, data.marketplaceId);
  data.lastActor = AuthConfig.getAlias();
  const apiURL = buildBaseUrl(region, stage).concat(`/${data.program}`);
  return await authAxios().post(apiURL, data);
}

export async function deleteProgram(programData: ProgramData, stage: string) {
  const data = { lastActor: AuthConfig.getAlias() };
  const region = resolveRoutingRegion(stage, programData.marketplaceId);
  const apiURL = buildBaseUrl(region, stage);
  return await authAxios().put(
    `${apiURL}/${programData.program}/${programData.programId}`,
    data
  );
}
// When EU tables become standalone, this should fetch from both ZAZ and FRA and merge results.
export async function fetchAllPrograms(
  props: FetchAllProgramsProps,
  stage: string
) {
  const region: BackendRegion = props.region === "EU" ? "ZAZ" : (props.region as BackendRegion);
  const apiURL = buildBaseUrl(region, stage);
  return await authAxios().get(`${apiURL}/${props.program}`);
}
